import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import clsx from 'clsx';
import { FC, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { CustomMuiIcon } from 'src/components/custom/CustomMuiIcon';
import { useLazyGetUserClientQuery } from 'src/redux/services/clients';
import { ProviderType } from 'src/redux/services/provider';
import { IUserProfile, useGetPublicExternalAccountsQuery } from 'src/redux/services/users';
import { ENoticeType, routes, subTabs, tabs } from '../../../enums';
import {
  exportToJson,
  formatDate,
  formatPhoneNumber,
  getClaimPrivacy,
  getImageURL,
  isOwnerOrEditor,
} from '../../../helpers';
import { ReactComponent as AvatarIcon } from '../../../icons/Avatar.svg';
import { TAppSlice } from '../../../redux/appSlice';
import { setNotice } from '../../../redux/noticesSlice';
import { useLazyDeleteAllSessionQuery } from '../../../redux/services/auth';
import { TUser } from '../../../redux/services/client';
import { useDeleteUsersMutation } from '../../../redux/services/owner';
import { useGetProfileFieldsQuery } from '../../../redux/services/settings';
import { useGetPrivateClaimsQuery } from '../../../redux/services/user';
import { RootState } from '../../../redux/store';
import { TUserSlice } from '../../../redux/userSlice';
import { CustomButton } from '../../custom/CustomButton';
import { CustomTypography } from '../../custom/CustomTypography';
import { ChangePasswordBlock } from '../../profile/ChangePasswordBlock';
import { ExternalAccount } from '../../profile/ExternalAccount';
import { EClaimPrivacy, PublicStatusPopover } from '../../profile/PublicStatusPopover';
import styles from './ApplicationUserInfo.module.css';
import { ChangePermissionsModal } from './ChangePermissionsModal';

type TApplicationUserInfoComponent = {
  startRoutePath: TAppSlice['startRoutePath'];
  roleInApp: TUserSlice['roleInApp'];
  loggedUserId: IUserProfile['id'];
  clientProfile: TAppSlice['clientProfile'];
};

const mapStateToProps = (state: RootState) => ({
  startRoutePath: state.app.startRoutePath,
  roleInApp: state.user.roleInApp,
  loggedUserId: state.user.profile.id,
  clientProfile: state.app.clientProfile,
});

const ApplicationUserInfoComponent: FC<TApplicationUserInfoComponent> = ({
  roleInApp,
  startRoutePath,
  loggedUserId,
  clientProfile,
}) => {
  const navigate = useNavigate();
  const {
    appId = '',
    clientId = '',
    userId = '',
  } = useParams<{ appId: string; clientId: string; userId: string }>();
  const PROJECT_NAME = clientProfile?.name || 'PROJECT_NAME';
  const [selectedUser, setSelectedUser] = useState<
    { user: Partial<TUser>; role: string } | null | undefined
  >(null);
  const [selectedPermission, setSelectedPermission] = useState<null | string>(null);
  const [changePermissionsModalOpen, setChangePermissionsModalOpen] = useState(false);
  const [deleteUsersModalOpen, setDeleteUsersModalOpen] = useState(false);
  const date = new Date(selectedUser?.user.birthdate || 0);
  const [getUser] = useLazyGetUserClientQuery();
  const [deleteAllSession] = useLazyDeleteAllSessionQuery();
  const { data: externalAccounts } = useGetPublicExternalAccountsQuery({
    user_id: String(userId),
    client_id: clientId || appId,
  });
  const { data: privateClaims } = useGetPrivateClaimsQuery(String(userId), {
    skip: !isOwnerOrEditor(roleInApp) || !userId,
  });
  const {
    public_profile_claims_oauth,
    public_profile_claims_gravatar,
    public_accounts_claims_oauth,
    public_accounts_claims_gravatar,
  } = privateClaims || {};
  const { data: profileFields } = useGetProfileFieldsQuery();
  const customFields =
    profileFields?.filter((field) => field.type === 'custom' && field.active) || [];
  const dispatch = useDispatch();
  const [deleteUsers, { isLoading: deleteUsersLoading }] = useDeleteUsersMutation();
  const deleteAppsWithUser = false;
  const disableButtons = deleteUsersLoading;

  const changePasswordLink =
    userId?.toString() === loggedUserId
      ? `/${routes.profile}/change-password`
      : `/${startRoutePath}/${appId}/${tabs.users}/${userId}/change-password`;

  const closeChangePermissionModal = () => {
    setSelectedPermission(null);
    setChangePermissionsModalOpen(false);
  };

  useEffect(() => {
    const start = async () => {
      const { data: user } = await getUser({
        clientId: clientId || appId,
        userId: String(userId),
      });

      setSelectedUser(user);
    };
    start();
  }, [clientId, userId]);

  const name = (
    (selectedUser?.user.given_name || '') +
    ' ' +
    (selectedUser?.user.family_name || '')
  ).trim();

  const clearUsers = async () => {
    setSelectedUser(null);
  };

  const handleDeleteButton = async () => {
    try {
      if (!selectedUser?.user?.id) return;
      setDeleteUsersModalOpen(true);
    } catch (e) {
      console.log('handleDeleteButton error', e);
    }
  };

  const handleDeleteUser = async () => {
    try {
      if (!selectedUser?.user?.id) return;

      await deleteUsers({
        delete_apps_with_user: deleteAppsWithUser,
        checked_ids: [selectedUser?.user.id],
      }).unwrap();

      const id: string =
        selectedUser.user.nickname || selectedUser.user.login || selectedUser.user.id.toString();

      dispatch(
        setNotice({
          type: ENoticeType.info,
          id: Math.random(),
          isRead: false,
          message: `Пользователь ${id} удален в ${PROJECT_NAME}.`,
          timestamp: new Date().toString(),
        }),
      );

      clearUsers();
      setDeleteUsersModalOpen(false);
      if (clientId) {
        navigate(`/${startRoutePath}/${appId}/${tabs.clients}/${clientId}${tabs.users}`);
      } else {
        navigate(`/${startRoutePath}/${appId}/${tabs.users}`);
      }
    } catch (e) {
      console.log('handleDeleteUsers error', e);
    }
  };

  const lineCustomFields = customFields
    .filter(
      (field) => selectedUser?.user.custom_fields && selectedUser.user.custom_fields[field.field],
    )
    .map((field) => {
      return (
        <div key={field.field} className={styles['info-item']}>
          <CustomTypography className={clsx('text-14', styles['info-item-title'])} color="grey">
            {field.title}
          </CustomTypography>
          <CustomTypography className={clsx('text-14', styles['info-item-value'])}>
            {selectedUser?.user.custom_fields && selectedUser.user.custom_fields[field.field]
              ? (selectedUser?.user?.custom_fields[field.field] as string)
              : 'Не задано'}
          </CustomTypography>
          {isOwnerOrEditor(roleInApp) && (
            <PublicStatusPopover
              claimPrivacy={getClaimPrivacy(
                field.field,
                public_profile_claims_oauth,
                public_profile_claims_gravatar,
              )}
              claims={field.field}
              userId={selectedUser?.user.id?.toString()}
            />
          )}
        </div>
      );
    });

  const handleEditNavigate = () => {
    if (clientId) {
      navigate(
        `/${startRoutePath}/${appId}/${tabs.clients}/${clientId}/${tabs.users}/${userId}/${subTabs.edit}`,
      );
    } else {
      navigate(`/${startRoutePath}/${appId}/${tabs.users}/${userId}/${subTabs.edit}`);
    }
  };

  return (
    <div className={clsx('wrapper-scroll')}>
      <div className={clsx('content')}>
        <div className={styles['panel-top']}>
          <Avatar
            className={styles['app-icon-wrapper']}
            src={getImageURL(selectedUser?.user?.picture)}
          >
            {!selectedUser?.user.picture && (
              <div className={styles['app-icon-default']}>
                {selectedUser?.user.nickname
                  ?.split(' ')
                  .map((name: string) => name[0]?.toUpperCase())
                  .join('')}
              </div>
            )}
          </Avatar>
          <div className={styles['name-wrapper']}>
            <CustomTypography
              className={clsx(
                'header-2-medium',
                'font-golos',

                styles['overflow-ellipsis'],
              )}
              component="div"
            >
              {!name && !selectedUser?.user?.profile_claims_privacy?.includes('nickname')
                ? 'Имя скрыто пользователем'
                : name || 'Нет имени'}
            </CustomTypography>
          </div>
        </div>
        <div className={styles.panel}>
          <div className={styles['panel-title']}>
            <CustomTypography className={clsx('header-3', 'font-golos')}>
              Основная информация
            </CustomTypography>
            {isOwnerOrEditor(roleInApp) && (
              <div style={{ marginLeft: 'auto' }} className={styles['margin-right']}>
                <Button variant="custom2" onClick={() => handleEditNavigate()}>
                  Изменить
                </Button>
              </div>
            )}
          </div>
          <div>
            <div className={styles['info-item']}>
              <div className={styles['flex-wrap']}>
                <CustomTypography
                  className={clsx('text-14', styles['info-item-title'])}
                  color="grey"
                >
                  ID пользователя
                </CustomTypography>
                <CustomTypography className={clsx('text-14', styles['info-item-value'])}>
                  {selectedUser?.user.id}
                </CustomTypography>
              </div>
              {isOwnerOrEditor(roleInApp) && (
                <PublicStatusPopover
                  claimPrivacy={EClaimPrivacy.publicGravatar}
                  claims="id"
                  disabled
                />
              )}
            </div>
            {!!(selectedUser?.user.nickname || '').trim() && (
              <div className={styles['info-item']}>
                <div className={styles['flex-wrap']}>
                  <CustomTypography
                    className={clsx('text-14', styles['info-item-title'])}
                    color="grey"
                  >
                    Публичное имя
                  </CustomTypography>
                  <CustomTypography className={clsx('text-14', styles['info-item-value'])}>
                    {(selectedUser?.user.nickname || '').trim()
                      ? selectedUser?.user.nickname
                      : 'Нет имени'}
                  </CustomTypography>
                </div>
                {isOwnerOrEditor(roleInApp) && (
                  <PublicStatusPopover
                    claimPrivacy={getClaimPrivacy(
                      'nickname',
                      public_profile_claims_oauth,
                      public_profile_claims_gravatar,
                    )}
                    claims="nickname"
                    userId={String(selectedUser?.user.id)}
                  />
                )}
              </div>
            )}
            {!!selectedUser?.user.picture && (
              <div className={styles['info-item']}>
                <CustomTypography
                  className={clsx('text-14', styles['info-item-title'])}
                  color="grey"
                >
                  Фото профиля
                </CustomTypography>
                {selectedUser?.user.picture ? (
                  <Avatar
                    src={getImageURL(selectedUser?.user?.picture)}
                    className={styles['user-icon-wrapper']}
                  />
                ) : (
                  <Avatar className={styles.avatar}>
                    <AvatarIcon />
                  </Avatar>
                )}
                {isOwnerOrEditor(roleInApp) && (
                  <PublicStatusPopover
                    claimPrivacy={getClaimPrivacy(
                      'picture',
                      public_profile_claims_oauth,
                      public_profile_claims_gravatar,
                    )}
                    claims="picture"
                    userId={String(selectedUser?.user.id)}
                  />
                )}
              </div>
            )}
            {!!name && (
              <div className={styles['info-item']}>
                <div className={styles['flex-wrap']}>
                  <CustomTypography
                    className={clsx('text-14', styles['info-item-title'])}
                    color="grey"
                  >
                    Имя и фамилия
                  </CustomTypography>
                  <CustomTypography className={clsx('text-14', styles['info-item-value'])}>
                    {name || 'Не задано'}
                  </CustomTypography>
                </div>
                {isOwnerOrEditor(roleInApp) && (
                  <PublicStatusPopover
                    claimPrivacy={getClaimPrivacy(
                      'family_name',
                      public_profile_claims_oauth,
                      public_profile_claims_gravatar,
                    )}
                    claims="family_name given_name"
                    userId={String(selectedUser?.user.id)}
                  />
                )}
              </div>
            )}
            {!!selectedUser?.user.login && (
              <div className={styles['info-item']}>
                <div className={styles['flex-wrap']}>
                  <CustomTypography
                    className={clsx('text-14', styles['info-item-title'])}
                    color="grey"
                  >
                    Логин
                  </CustomTypography>
                  <CustomTypography className={clsx('text-14', styles['info-item-value'])}>
                    {selectedUser?.user.login || 'Не задано'}
                  </CustomTypography>
                </div>
                {isOwnerOrEditor(roleInApp) && (
                  <PublicStatusPopover
                    claimPrivacy={getClaimPrivacy(
                      'login',
                      public_profile_claims_oauth,
                      public_profile_claims_gravatar,
                    )}
                    claims="login"
                    userId={String(selectedUser?.user.id)}
                  />
                )}
              </div>
            )}
            {!!selectedUser?.user.birthdate && (
              <div className={styles['info-item']}>
                <div className={styles['flex-wrap']}>
                  <CustomTypography
                    className={clsx('text-14', styles['info-item-title'])}
                    color="grey"
                  >
                    Дата рождения
                  </CustomTypography>
                  <CustomTypography className={clsx('text-14', styles['info-item-value'])}>
                    {selectedUser?.user.birthdate ? formatDate(date) : 'Не задано'}
                  </CustomTypography>
                </div>
                {isOwnerOrEditor(roleInApp) && (
                  <PublicStatusPopover
                    claimPrivacy={getClaimPrivacy(
                      'birthdate',
                      public_profile_claims_oauth,
                      public_profile_claims_gravatar,
                    )}
                    claims="birthdate"
                    userId={String(selectedUser?.user.id)}
                  />
                )}
              </div>
            )}
            {!!selectedUser?.user.email && (
              <div className={styles['info-item']}>
                <div className={styles['flex-wrap']}>
                  <CustomTypography
                    className={clsx('text-14', styles['info-item-title'])}
                    color="grey"
                  >
                    Электронная почта
                  </CustomTypography>
                  <CustomTypography className={clsx('text-14', styles['info-item-value'])}>
                    {selectedUser?.user.email ? selectedUser.user.email : 'Не задано'}
                  </CustomTypography>
                </div>
                {isOwnerOrEditor(roleInApp) && (
                  <PublicStatusPopover
                    claimPrivacy={getClaimPrivacy(
                      'email',
                      public_profile_claims_oauth,
                      public_profile_claims_gravatar,
                    )}
                    claims="email"
                    userId={String(selectedUser?.user.id)}
                  />
                )}
              </div>
            )}
            {!!selectedUser?.user.phone_number && (
              <div className={styles['info-item']}>
                <div className={styles['flex-wrap']}>
                  <CustomTypography
                    className={clsx('text-14', styles['info-item-title'])}
                    color="grey"
                  >
                    Номер телефона
                  </CustomTypography>
                  <CustomTypography className={clsx('text-14', styles['info-item-value'])}>
                    {selectedUser?.user.phone_number
                      ? formatPhoneNumber(selectedUser.user.phone_number)
                      : 'Не задано'}
                  </CustomTypography>
                </div>
                <PublicStatusPopover
                  claimPrivacy={getClaimPrivacy(
                    'phone_number',
                    public_profile_claims_oauth,
                    public_profile_claims_gravatar,
                  )}
                  claims="phone_number"
                  userId={String(selectedUser?.user.id)}
                />
              </div>
            )}
            {lineCustomFields.length > 0 && (
              <div>
                <CustomTypography className={clsx('header-3', 'font-golos')}>
                  Дополнительная информация
                </CustomTypography>
                {lineCustomFields}
              </div>
            )}
          </div>
        </div>
        <div className={styles.panel}>
          <div className={styles.justify}>
            <CustomTypography
              style={{ marginBottom: 24 }}
              className={clsx('text-17-regular', 'font-golos')}
            >
              Идентификаторы
            </CustomTypography>
          </div>
          <div>
            {selectedUser?.user &&
              externalAccounts
                ?.filter((ea) => ea.type !== ProviderType.EMAIL && ea.type !== ProviderType.PHONE)
                .map((account) => (
                  <ExternalAccount
                    account={account}
                    userProfile={selectedUser.user}
                    public_accounts_claims_oauth={public_accounts_claims_oauth}
                    public_accounts_claims_gravatar={public_accounts_claims_gravatar}
                    key={(account.sub || '') + (account.issuer || '') + (account.type || '')}
                  />
                ))}
          </div>
        </div>
        {isOwnerOrEditor(roleInApp) && (
          <ChangePasswordBlock
            passwordUpdateDate={new Date(selectedUser?.user.password_updated_at || '')}
            navigateTo={changePasswordLink}
          />
        )}
        {isOwnerOrEditor(roleInApp) && (
          <Accordion className={clsx(styles.panel, styles.accordion)}>
            <AccordionSummary
              className={styles['accorion-summary']}
              classes={{ content: styles['accorion-summary-content'] }}
              expandIcon={
                <IconButton size="large" disableRipple={false}>
                  <CustomMuiIcon Icon={KeyboardArrowDownOutlinedIcon} />
                </IconButton>
              }
            >
              <CustomTypography className={clsx('text-17-regular', 'font-golos')}>
                Другие действия
              </CustomTypography>
            </AccordionSummary>
            <AccordionDetails className={styles['accordion-details']}>
              <Button
                variant="custom2"
                className={styles['margin-bottom']}
                onClick={async () => {
                  await deleteAllSession(selectedUser?.user.id);
                }}
              >
                Выйти со всех устройств
              </Button>
              <Button
                onClick={() => exportToJson({ ...selectedUser }, 'profile.json')}
                variant="custom2"
                className={styles['margin-bottom']}
              >
                Скачать данные
              </Button>
              {selectedUser?.user.id !== 1 &&
                selectedUser?.user.id !== parseInt(loggedUserId as string, 10) && (
                  <Button
                    onClick={handleDeleteButton}
                    variant="custom2"
                    className={styles['margin-bottom']}
                  >
                    Удалить аккаунт
                  </Button>
                )}
            </AccordionDetails>
          </Accordion>
        )}
      </div>
      <Modal open={changePermissionsModalOpen} onClose={closeChangePermissionModal}>
        <ChangePermissionsModal
          selectedClientId={clientId}
          selectedUserToActions={selectedUser?.user as Partial<TUser> | null}
          selectedUserRoleFromProps={selectedUser?.role}
          closeChangePermissionModal={closeChangePermissionModal}
          selectedPermission={selectedPermission}
          setSelectedPermission={setSelectedPermission}
          setChangePermissionsModalOpen={setChangePermissionsModalOpen}
        />
      </Modal>

      <Modal open={deleteUsersModalOpen} onClose={() => setDeleteUsersModalOpen(false)}>
        <div className={styles['modal-wrapper']}>
          <CustomTypography
            style={{ marginBottom: 24 }}
            className={clsx('header-2-medium', 'font-golos')}
          >
            Удалить аккаунт {PROJECT_NAME}
          </CustomTypography>
          <div>
            <CustomTypography className={clsx('text-14')}>
              Аккаунт{' '}
              {selectedUser?.user?.nickname || selectedUser?.user?.login || selectedUser?.user?.id}{' '}
              будет удален. Данные, связанные с аккаунтом, удалятся навсегда. Пользователь потеряет
              доступ к приложениям, в которых для входа использовал аккаунт {PROJECT_NAME}.
            </CustomTypography>
            <CustomTypography style={{ marginTop: 16 }} className={clsx('text-14')}>
              Вместе с аккаунтом будут удалены приложения, в которых удаляемый пользователь является
              владельцем.
            </CustomTypography>
          </div>
          <div style={{ marginTop: 32, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              disabled={disableButtons}
              onClick={() => setDeleteUsersModalOpen(false)}
              variant="custom"
              color="secondary"
            >
              Отмена
            </Button>
            <CustomButton
              loading={disableButtons}
              disabled={disableButtons}
              onClick={handleDeleteUser}
              style={{ marginLeft: 24, background: '#FC545C' }}
            >
              Удалить аккаунт
            </CustomButton>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export const ApplicationUserInfo = connect(mapStateToProps)(ApplicationUserInfoComponent);
